import { ApplicationReason } from "./ApplicationSummary"
import { Occupancy, OpportunityStage, Persona, PropertyAddress } from "./FullApplication"

export interface ApplicationSummaryEnhanced {
  mortgageApplicationId?: number
  applicationId?: string
  friendlyId?: string
  applicationReason?: ApplicationReason
  term?: number
  createdOn?: string
  lastModifiedDate?: string
  userId?: number
  // NEW
  offeredAppointment: boolean
  mipUrl: string | undefined
  hasAppointments: boolean
  propertyValue: number | undefined
  firstTimeBuyer: boolean
  mortgageBalance: number | undefined
  purchaseType: SfPurchaseType | undefined
  propertyAddress: PropertyAddress | undefined
  deposit: number | undefined
  purchasePrice: number | undefined
  stage: OpportunityStage | undefined
  appointments: ApplicationAppointments[]
  lender: string | undefined
  remortgageReason: string | undefined
  persona: Persona | undefined
  occupancy: Occupancy | undefined
  initialPeriodEndDate: string | undefined
  retentionApplication: boolean
  service: Service | undefined
  previousOpportunityOwner: string | undefined
  tls: {
    name: string | undefined
    appointmentOffered: boolean | undefined
    colour: string | undefined
    tier: string | undefined
    team: string | undefined
  }
  owner: Owner
  cohort: string
  completionDate: string
  mortgagePreferences: MortgagePreferences
  debtConsolidation?: DebtConsolidation
  caseComplexities: CaseComplexities
}

export interface CaseComplexities {
  isLendingIntoRetirement: boolean
}

export enum MortgageType {
  FixedRateMortgage = "Fixed Rate mortgage",
  TrackerMortgage = "Tracker Mortgage",
}
export enum InitialPeriod {
  OneYear = "1 year",
  TwoYears = "2 years",
  ThreeYears = "3 years",
  FiveYears = "5 years",
  SevenYears = "7 years",
  TenYears = "10 years",
  OverTenYears = "Over 10 years",
  ERCFree = "ERC-free",
}

interface MortgagePreferenceReason {
  reason: string | null
  customReason: string | null
}

export enum RepaymentMethod {
  Repayment = "Repayment",
  InterestOnly = "Interest only",
  PartAndPart = "Part and part",
}

export enum Fees {
  PayUpFront = "Pay up front",
  AddToTheLoan = "Add to the loan",
  NoFees = "No fees",
}
export enum ConsolidationReasoning {
  ReducesMonthlyOutgoings = "it reduces monthly outgoings, improving disposable income and quality of life",
  ReducesMonthlyCommitments = "the reduction in monthly commitments is essential for ongoing affordability and stability",
  ReducesCostOfBorrowing = "it reduces the total cost of borrowing, and interest payable, compared to your current arrangements",
  Other = "Other",
}

export interface MortgagePreferences {
  mortgageType: MortgagePreferenceReason & {
    type: MortgageType | null
  }
  initialPeriod: MortgagePreferenceReason & {
    period: InitialPeriod | null
  }
  term: MortgagePreferenceReason & {
    years: number | null
    months: number | null
  }
  repaymentMethod: MortgagePreferenceReason & {
    method: RepaymentMethod | null
  }
  // Leaving like this in case we expand to have a reason later
  fees: {
    type: Fees | null
  }
  budget: {
    amount: number | null
  }
}

export interface DebtConsolidation {
  reason: ConsolidationReasoning | null
  customReason: string | null
  debtConsolidationAppropriate: boolean | null
}

export type Owner = {
  name: string | undefined
  mortgageExpertImage: string | undefined
}
export interface Service {
  fee: number | undefined
  level: "Free" | "Pro" | "Specialist" | undefined
  checkoutSessionUrl: string | undefined
  status: PaymentStatus | undefined
}

export enum PaymentStatus {
  NotNeededYet = "Not needed yet",
  PaymentRequired = "Payment required",
  Paid = "Paid",
  RefundedInPart = "Refunded in part",
  RefundedInFull = "Refunded in full",
  FeeRemoved = "Fee removed",
}

export interface ApplicationAppointments {
  acuityAppointmentId: number | undefined
  appointmentTypeId: number | undefined
  appointmentDate: string | undefined
  assignee: string | undefined
  calendlyEventDuration: number | undefined
  calendlyEventName: string | undefined
  createdOn: string | undefined
  isCancelled: boolean | undefined
}

export enum SfPurchaseType {
  FirstTimeBuyer = "First time buyer",
  MovingHome = "Moving home",
  SecondHome = "Second home",
  NextTimeBuyer = "Next time buyer",
}
